import { Grid, Box, Typography, Paper, CircularProgress, alpha, useTheme, Chip } from '@mui/material';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useSnackbar } from 'notistack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ProductDescription from './ProductDescription';
import ShipmentTypeDetails from './ShipmentTypeDetails';
import BoxList from './BoxList';
import TotalValues from './TotalValue';
import CreateShipmentContext from '../../../context/CreateShipment';
import { createShipmentContextType } from '../../../types/shipment';
import { getRates, setCreateShipmentCSP } from '../../../store/reducers/GetRates';
import { useDispatch, useSelector } from 'react-redux';
import { SHIPMENT_TYPES } from '../../GetRatesV2/Constant';
import { AddressType, CSPChargeType } from '../../../store/reducers/GetRates/Type';
import { AppDispatch, RootStateType } from '../../../store';
import NoCSPFound from '../../GetRatesV2/CSPList/NoCSPFound/NoCSPFound';
import ViewAction from '../../ViewSingleShipment/ViewAction';
import PackageAiValidationAlert from '../../../components/PackageAiValidationAlert';
import { useAiValidation } from '../../../hooks/useAiValidation';
import { isDutyChargesAllowedSubdomain } from '../../../utils/getSubdomain';
import ShipmentServices from './ShipmentServices';
import SbButton from '../../../components/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { getAddressObj, getSingleShipmentPackagePayload } from '@Services/getShipmentPayload';
import { DraftShipmentCreatePayload } from '@Reducers/DraftShipment/Type';
import { createDraftShipment, updateDraftShipmentById } from '@Reducers/DraftShipment/actions';
import {
  resolveShipmentValueInEur,
  getEuComplianceRule,
  isComplianceRequired,
  isEUCountry,
} from '../../../utils/euCompliance';
import { useEurExchangeRate } from '../../../utils/currencyConversion';
import { isExportShipment, getCountryName } from '../../../utils/exportShipment';
import { userLogin } from '@Services/hasAdminAccess';
import { isUSDestination, validateCpscCompliance } from '../../../utils/usCompliance';
import { formatPendingRequirementLabel } from '../../../utils/fieldLabels';
import { getDutyCharge } from '../../../utils/pricing';

function getAddressObjLocal({ pincode, city, country, state }: AddressType) {
  return { city, state, country, pincode };
}

const getDutyChargeType = (dutyCharge: any): string => {
  if (!dutyCharge) return '';
  if (typeof dutyCharge === 'string') return dutyCharge;
  if (typeof dutyCharge === 'object' && typeof dutyCharge.type === 'string') return dutyCharge.type;
  return '';
};

function ShipmentDetails({
  removeShipmentInfo,
  showViewMoreAction,
  isShipmentDetailsWriteAllowed,
}: {
  removeShipmentInfo?: boolean;
  showViewMoreAction?: boolean;
  isShipmentDetailsWriteAllowed?: boolean;
}) {
  const [viewMore, setViewMore] = useState<boolean>(true);
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const servicesRef = useRef<HTMLDivElement>(null);
  const dutyTaxSectionRef = useRef<HTMLDivElement>(null);
  const { enqueueSnackbar } = useSnackbar();

  const {
    setActiveStep,
    showRates,
    setShowRates,
    setSelectedCSP,
    selectedCSP,
    chargeableWeight,
    boxes,
    setBoxes,
    setSelectedBox,
    setSelectedProduct,
    consignee,
    shipper,
    totalShipmentValue: value,
    shipmentType,
    setShipmentType,
    boxValidation,
    userId,
    pickupType: { selectedWearhouseAddress },
    pickupType,
    packageData,
    extraDetails,
    notes,
    ratesSnapshot,
    setRatesSnapshot,
    isProductFormInvalid,
    isHSNValidating,
  } = React.useContext(CreateShipmentContext) as createShipmentContextType;

  const { validationStates, validate } = useAiValidation();

  const rates = useSelector((state: RootStateType) => state.getRates);
  const { chareableWeight = { maxChargeableWt: 0 }, CSPChargesData } = useSelector(
    (state: RootStateType) => state.getRates,
  );
  const { shipmentById } = useSelector((state: RootStateType) => state.adminShipment);
  const { selectedDraftShipmentId: draftId } = useSelector((state: RootStateType) => state.DraftShipment);
  const organizationCountry = useSelector((state: RootStateType) => state.orgSetup.organizationData);
  const countriesNameObj = useSelector((state: RootStateType) => state.global.countriesNameObj) || {};
  const countries = useSelector((state: RootStateType) => state.global.countries) || [];

  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '', _country } = organizationData || {};

  const orgCountryName = _country?.name?.trim() || organizationCountry?._country?.name?.trim() || '';

  const isShipmentExport = isExportShipment({ orgCountryName, shipper, consignee, shipmentById });
  const consigneeCountry = getCountryName(consignee);

  console.log('chargeableWeight:', chargeableWeight);

  const destCountry = consigneeCountry || getCountryName(shipmentById?._consigneeId) || '';
  const isEUDestination = useMemo(() => isEUCountry(destCountry), [destCountry]);
  const { rate: eurRate } = useEurExchangeRate({ currency: shipmentType?.currency, isEU: isEUDestination });
  const shipmentValueEur = useMemo(() => {
    return resolveShipmentValueInEur(boxes, shipmentType?.currency, value, eurRate);
  }, [boxes, shipmentType?.currency, value, eurRate]);

  const eurEquivalentText = useMemo(() => {
    if (!isEUDestination || !eurRate || (shipmentType?.currency || '').toUpperCase() === 'EUR') return '';
    const eurVal = (Number(value || 0) * eurRate).toFixed(2);
    return `(≈ €${eurVal})`;
  }, [isEUDestination, eurRate, shipmentType?.currency, value]);

  const isUserLogin = userLogin();
  const complianceRequired = isComplianceRequired(isUserLogin);

  const euComplianceRule = useMemo(() => {
    return getEuComplianceRule({
      destCountry,
      shipmentMode: shipmentType?.shipmentMode,
      shipmentValueEur,
      isUserLogin,
    });
  }, [destCountry, shipmentType?.shipmentMode, shipmentValueEur, isUserLogin]);

  const isUSConsignee = useMemo(() => isUSDestination(destCountry), [destCountry]);

  // Clean up EU compliance fields when they become hidden
  useEffect(() => {
    if (!euComplianceRule.isVisible && boxes?.length) {
      let hasComplianceFields = false;
      for (const b of boxes) {
        for (const p of b.products || []) {
          if (p.merchantProductId || p.standardManufacturerProductId || p.nonStandardManufacturerProductId) {
            hasComplianceFields = true;
            break;
          }
        }
        if (hasComplianceFields) break;
      }
      if (hasComplianceFields) {
        setBoxes(
          boxes.map((b: any) => ({
            ...b,
            products: (b.products || []).map((p: any) => ({
              ...p,
              merchantProductId: '',
              standardManufacturerProductId: '',
              nonStandardManufacturerProductId: '',
            })),
          })),
        );
      }
    }
  }, [euComplianceRule.isVisible, boxes, setBoxes]);

  // Clean up US CPSC compliance fields when country is not US
  useEffect(() => {
    if (!isUSConsignee && boxes?.length) {
      let hasCpsc = false;
      for (const b of boxes) {
        for (const p of b.products || []) {
          if (p.cpscCompliance || p.compliance?.us) {
            hasCpsc = true;
            break;
          }
        }
        if (hasCpsc) break;
      }
      if (hasCpsc) {
        setBoxes(
          boxes.map((b: any) => ({
            ...b,
            products: (b.products || []).map((p: any) => {
              const cleanP = { ...p };
              delete cleanP.cpscCompliance;
              if (cleanP.compliance?.us) {
                delete cleanP.compliance.us;
              }
              return cleanP;
            }),
          })),
        );
      }
    }
  }, [isUSConsignee, boxes, setBoxes]);

  // Stable shipmentPackage validation payload builder
  const packagePayload = useMemo(() => {
    const purpose =
      extraDetails?.purposeOfShipment && String(extraDetails.purposeOfShipment).trim()
        ? String(extraDetails.purposeOfShipment).trim()
        : 'FOR_SALE';

    const insurance = typeof packageData?.insurance === 'boolean' ? packageData.insurance : false;
    const unitOfMeasurement = shipmentType?.unit || packageData?.unitOfMeasurement || 'kg-cm';
    const currency = shipmentType?.currency || packageData?.currency || 'INR';

    const boxPayload = (boxes || []).map(b => ({
      length: Number(b.length || 0),
      width: Number(b.width || 0),
      height: Number(b.height || 0),
      weight: Number(b.weight || 0),
      products: (b.products || []).map((p: any) => ({
        description: String(p.description || '').trim(),
        quantity: Number(p.quantity || 0),
        unitValue: Number(p.unitValue || 0),
        HSNName: p.HSNName ? String(p.HSNName).trim() : '',
        HSNCode: p.HSNCode ? String(p.HSNCode).trim() : '',
        productId: p.productId ? String(p.productId).trim() : '',
        productIdType: p.productIdType ? String(p.productIdType).trim() : 'SKU',
        merchantProductId: p.merchantProductId ? String(p.merchantProductId).trim() : '',
        standardManufacturerProductId:
          p.standardManufacturerProductId && String(p.standardManufacturerProductId).trim() !== ''
            ? String(p.standardManufacturerProductId).trim()
            : 'NO',
        nonStandardManufacturerProductId: p.nonStandardManufacturerProductId
          ? String(p.nonStandardManufacturerProductId).trim()
          : '',
      })),
    }));

    return {
      purposeOfShipment: purpose,
      insurance,
      unitOfMeasurement,
      currency,
      box: boxPayload,
      incoterm: shipmentType?.incoterm || 'FOB',
      freight: Number(shipmentType?.freight || 0),
      freightInsurance: Number(shipmentType?.freightInsurance || 0),
      iossNumber: shipmentType?.iossNumber ? String(shipmentType.iossNumber).trim() : '',
      recipientVatTaxId: shipmentType?.recipientVatTaxId ? String(shipmentType.recipientVatTaxId).trim() : '',
      iorEoriNumber: shipmentType?.iorEoriNumber ? String(shipmentType.iorEoriNumber).trim() : '',
    };
  }, [
    extraDetails?.purposeOfShipment,
    packageData?.insurance,
    packageData?.unitOfMeasurement,
    packageData?.currency,
    shipmentType?.unit,
    shipmentType?.currency,
    shipmentType?.incoterm,
    extraDetails?.purposeOfShipment,
    shipmentType?.freight,
    shipmentType?.freightInsurance,
    shipmentType?.iossNumber,
    shipmentType?.recipientVatTaxId,
    shipmentType?.iorEoriNumber,
    boxes,
    euComplianceRule.isVisible,
  ]);

  // Trigger centralized AI package validation
  useEffect(() => {
    validate('shipmentPackage', 'shipmentPackage', packagePayload);
  }, [packagePayload, validate]);

  // Stable snapshot of all dependencies that affect Get Rates payload
  const currentRatesSnapshot = useMemo(() => {
    const sourceAddr = selectedWearhouseAddress?.label ? selectedWearhouseAddress : shipper;
    const destAddr = consignee;
    return JSON.stringify({
      source: {
        city: (sourceAddr?.city || '').toString().trim().toLowerCase(),
        state: (sourceAddr?.state || '').toString().trim().toLowerCase(),
        country: (sourceAddr?.country || '').toString().trim().toLowerCase(),
        pincode: (sourceAddr?.pincode || '').toString().trim(),
      },
      destination: {
        city: (destAddr?.city || '').toString().trim().toLowerCase(),
        state: (destAddr?.state || '').toString().trim().toLowerCase(),
        country: (destAddr?.country || '').toString().trim().toLowerCase(),
        pincode: (destAddr?.pincode || '').toString().trim(),
      },
      shipmentType: shipmentType?.type || '',
      shipmentUnit: shipmentType?.unit || '',
      currency: shipmentType?.currency || '',
      userId: userId || null,
      chargeableWeight: Number(chareableWeight?.maxChargeableWt || 0),
      totalShipmentValue: Number(value || 0),
      insurance: Boolean(packageData?.insurance),
      packaging: Boolean(packageData?.packaging),
      dropOff: pickupType?.type === 'dropOff',
      boxes: (boxes || []).map(b => ({
        length: Number(b.length || 0),
        width: Number(b.width || 0),
        height: Number(b.height || 0),
        weight: Number(b.weight || 0),
        boxCopyCount: Number(b.boxCopyCount || 0),
        products: (b.products || []).map((p: any) => ({
          hsnCode: (p.HSNCode || p.hsnCode || '').toString().trim(),
          qty: Number(p.quantity !== undefined && p.quantity !== null ? p.quantity : p.qty || 0),
          value: Number(p.unitValue !== undefined && p.unitValue !== null ? p.unitValue : p.value || 0),
          description: (p.description || '').toString().trim(),
        })),
      })),
    });
  }, [
    selectedWearhouseAddress,
    shipper,
    consignee,
    shipmentType?.type,
    shipmentType?.unit,
    shipmentType?.currency,
    userId,
    chareableWeight?.maxChargeableWt,
    value,
    packageData?.insurance,
    packageData?.packaging,
    pickupType?.type,
    boxes,
  ]);

  const isRatesStale = Boolean(ratesSnapshot && ratesSnapshot !== currentRatesSnapshot);

  // If rates become stale, immediately invalidate selected CSP so user cannot proceed with obsolete service
  useEffect(() => {
    if (isRatesStale && selectedCSP) {
      setSelectedCSP(null);
    }
  }, [isRatesStale, selectedCSP, setSelectedCSP]);

  // When rates arrive from getRates, save snapshot, show services inline, and scroll smoothly
  useEffect(() => {
    if (rates.redirectToCreateShipmentCSP) {
      setShowRates(true);
      setRatesSnapshot(currentRatesSnapshot);
      dispatch(setCreateShipmentCSP(false));
      setTimeout(() => {
        servicesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  }, [rates.redirectToCreateShipmentCSP, currentRatesSnapshot]);

  // Hydrate snapshot if rates already exist in state but snapshot not yet recorded
  useEffect(() => {
    if (showRates && CSPChargesData?.charges?.length && !ratesSnapshot && !rates.loading) {
      setRatesSnapshot(currentRatesSnapshot);
    }
  }, [showRates, CSPChargesData?.charges?.length, ratesSnapshot, currentRatesSnapshot, rates.loading]);

  const pendingRequirements = useMemo(() => {
    const list: { label: string; boxIndex?: number }[] = [];

    if (!boxes || boxes.length === 0) {
      list.push({ label: 'Boxes (At least 1 box is required)' });
      return list;
    }

    if (isHSNValidating) {
      list.push({ label: 'HSN code is currently verifying' });
    }

    if (isProductFormInvalid) {
      list.push({ label: 'Complete active product entry or cancel edit' });
    }

    boxes.forEach((box, idx) => {
      const boxNum = idx + 1;
      const weight = Number((box.weight && `${box.weight}`) || 0);
      const length = Number((box.length && `${box.length}`) || 0);
      const width = Number((box.width && `${box.width}`) || 0);
      const height = Number((box.height && `${box.height}`) || 0);

      if (!weight || weight <= 0) {
        list.push({ label: formatPendingRequirementLabel('weight', { boxNumber: boxNum }), boxIndex: idx });
      }

      if (!length || length <= 0) {
        list.push({ label: formatPendingRequirementLabel('length', { boxNumber: boxNum }), boxIndex: idx });
      }
      if (!width || width <= 0) {
        list.push({ label: formatPendingRequirementLabel('width', { boxNumber: boxNum }), boxIndex: idx });
      }
      if (!height || height <= 0) {
        list.push({ label: formatPendingRequirementLabel('height', { boxNumber: boxNum }), boxIndex: idx });
      }

      if (!box.products || box.products.length === 0) {
        list.push({
          label: `Box ${boxNum} — Declared Goods (At least 1 declared item is required)`,
          boxIndex: idx,
        });
      } else {
        box.products.forEach((p: any, pIdx: number) => {
          const rowNum = pIdx + 1;
          if (!p.description?.trim()) {
            list.push({
              label: formatPendingRequirementLabel('description', { boxNumber: boxNum, productRowNumber: rowNum }),
              boxIndex: idx,
            });
          }
          if (!p.quantity || Number(p.quantity) <= 0) {
            list.push({
              label: formatPendingRequirementLabel('quantity', { boxNumber: boxNum, productRowNumber: rowNum }),
              boxIndex: idx,
            });
          }
          if (p.unitValue === undefined || p.unitValue === null || Number(p.unitValue) <= 0) {
            list.push({
              label: formatPendingRequirementLabel('unitValue', { boxNumber: boxNum, productRowNumber: rowNum }),
              boxIndex: idx,
            });
          }
          if (isShipmentExport && !p.HSNCode?.trim()) {
            list.push({
              label: formatPendingRequirementLabel('HSNCode', { boxNumber: boxNum, productRowNumber: rowNum }),
              boxIndex: idx,
            });
          }

          if (complianceRequired && euComplianceRule.isRequired) {
            if (!p.merchantProductId?.trim()) {
              list.push({
                label: formatPendingRequirementLabel('merchantProductId', {
                  boxNumber: boxNum,
                  productRowNumber: rowNum,
                }),
                boxIndex: idx,
              });
            }
            if (!p.nonStandardManufacturerProductId?.trim()) {
              list.push({
                label: formatPendingRequirementLabel('nonStandardManufacturerProductId', {
                  boxNumber: boxNum,
                  productRowNumber: rowNum,
                }),
                boxIndex: idx,
              });
            }
          }

          if (complianceRequired && isUSConsignee) {
            const cpscVal = validateCpscCompliance(p.cpscCompliance, true, true);
            if (!cpscVal.isValid) {
              Object.keys(cpscVal.errors).forEach(errKey => {
                list.push({
                  label: formatPendingRequirementLabel(errKey, {
                    boxNumber: boxNum,
                    productRowNumber: rowNum,
                  }),
                  boxIndex: idx,
                });
              });
            }
          }
        });
      }
    });

    // Shipment Mode-driven requirements
    if (!shipmentType?.shipmentMode?.trim()) {
      list.push({ label: formatPendingRequirementLabel('shipmentMode') });
    }

    const isB2BMode = (shipmentType?.shipmentMode || '').toUpperCase() === 'B2B';
    const vatIdVal = (shipmentType?.recipientVatTaxId || extraDetails?.recipientVatTaxId || '').trim();
    if (isB2BMode && !vatIdVal) {
      list.push({ label: formatPendingRequirementLabel('recipientVatTaxId') });
    }

    const isEcomMode = (shipmentType?.shipmentMode || '').toUpperCase() === 'ECOM';
    const purposeVal = (extraDetails?.purposeOfShipment || '').trim();
    if (isEcomMode && !purposeVal) {
      list.push({ label: formatPendingRequirementLabel('purposeOfShipment') });
    }

    // Incoterm-driven requirements
    const incotermVal = (shipmentType?.incoterm || extraDetails?.incoterm || '').trim().toUpperCase();
    if (!incotermVal) {
      list.push({ label: formatPendingRequirementLabel('incoterm') });
    } else if (incotermVal === 'CIF') {
      const frVal =
        shipmentType?.freight !== undefined && shipmentType?.freight !== ''
          ? shipmentType.freight
          : extraDetails?.freight;
      const insVal =
        shipmentType?.freightInsurance !== undefined && shipmentType?.freightInsurance !== ''
          ? shipmentType.freightInsurance
          : extraDetails?.freightInsurance;
      if (!frVal || Number(frVal) <= 0) {
        list.push({ label: formatPendingRequirementLabel('freight') });
      }
      if (!insVal || Number(insVal) <= 0) {
        list.push({ label: formatPendingRequirementLabel('freightInsurance') });
      }
    } else if (incotermVal === 'CNF') {
      const frVal =
        shipmentType?.freight !== undefined && shipmentType?.freight !== ''
          ? shipmentType.freight
          : extraDetails?.freight;
      if (!frVal || Number(frVal) <= 0) {
        list.push({ label: formatPendingRequirementLabel('freight') });
      }
    }

    return list;
  }, [
    boxes,
    isShipmentExport,
    isProductFormInvalid,
    isHSNValidating,
    shipmentType?.shipmentMode,
    shipmentType?.recipientVatTaxId,
    extraDetails?.recipientVatTaxId,
    shipmentType?.incoterm,
    extraDetails?.incoterm,
    shipmentType?.freight,
    extraDetails?.freight,
    shipmentType?.freightInsurance,
    extraDetails?.freightInsurance,
    euComplianceRule.isRequired,
    isUSConsignee,
    complianceRequired,
  ]);

  const isCalculateRatesDisabled =
    rates.loading || isHSNValidating || isProductFormInvalid || !boxValidation || pendingRequirements.length > 0;

  const handleCalculateRatesClick = () => {
    if (!boxValidation || pendingRequirements.length > 0) {
      window.dispatchEvent(new CustomEvent('validate-shipment-details'));
      if (pendingRequirements.length > 0) {
        const first = pendingRequirements[0];
        enqueueSnackbar(`Please complete details: ${first.label}`, { variant: 'warning' });
        if (first.boxIndex !== undefined && boxes[first.boxIndex]) {
          setSelectedBox(boxes[first.boxIndex]);
        }
        const vatField = document.querySelector('input[name="recipientVatTaxId"]') as HTMLElement;
        if (first.label.includes('Recipient VAT') && vatField) {
          vatField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          vatField.focus();
        } else {
          const boxDimensionsEl = document.querySelector('.sb-textfield-wrapper');
          boxDimensionsEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        enqueueSnackbar('Please verify all box details and products before calculating rates.', { variant: 'warning' });
      }
      return;
    }
    handleGetRates();
  };

  function handleGetRates() {
    if (isShipmentExport) {
      let missingProduct: any = null;
      let missingBox: any = null;
      let missingProductIndex = -1;

      for (const box of boxes) {
        if (box.products) {
          const idx = box.products.findIndex(p => !p.HSNCode?.trim());
          if (idx !== -1) {
            missingProduct = box.products[idx];
            missingBox = box;
            missingProductIndex = idx;
            break;
          }
        }
      }

      if (missingProduct) {
        setSelectedBox(missingBox);
        setSelectedProduct({
          ...missingProduct,
          id: missingProductIndex.toString(),
          isUpdate: true,
        });
        return;
      }

      const getCountryCode = (countryName: string): string => {
        if (!countryName) return '';
        const trimmedName = countryName.trim();
        if (countriesNameObj[trimmedName]?.countryCode) {
          return countriesNameObj[trimmedName].countryCode;
        }
        const keys = Object.keys(countriesNameObj);
        const matchedKey = keys.find(k => k.toLowerCase() === trimmedName.toLowerCase());
        if (matchedKey && countriesNameObj[matchedKey]?.countryCode) {
          return countriesNameObj[matchedKey].countryCode;
        }
        const foundCountry = countries.find(c => {
          if (c.name?.toLowerCase() === trimmedName.toLowerCase()) return true;
          if (c.alternateNames?.some(alt => alt.toLowerCase() === trimmedName.toLowerCase())) return true;
          return false;
        });
        return foundCountry?.countryCode || '';
      };

      const sourceAddr = selectedWearhouseAddress?.label ? selectedWearhouseAddress : shipper;
      const destAddr = consignee;

      const internationalPayload = {
        destination: {
          city: destAddr.city || '',
          state: destAddr.state || '',
          country: destAddr.country || '',
          countryCode: getCountryCode(destAddr.country),
          pincode: destAddr.pincode || '',
        },
        source: {
          city: sourceAddr.city || '',
          state: sourceAddr.state || '',
          country: sourceAddr.country || '',
          countryCode: getCountryCode(sourceAddr.country),
          pincode: sourceAddr.pincode || '',
        },
        shipmentType: shipmentType.type,
        userId,
        weight: chareableWeight?.maxChargeableWt || 0,
        value,
        package: {
          box: boxes.flatMap(box => {
            const boxWeight = box.weight ? box.weight.toString() : '0';
            const boxLength = box.length ? box.length.toString() : '0';
            const boxWidth = box.width ? box.width.toString() : '0';
            const boxHeight = box.height ? box.height.toString() : '0';

            return {
              length: boxLength,
              width: boxWidth,
              height: boxHeight,
              weight: boxWeight,
              products: (box.products || []).map((product: any) => {
                const qtyVal =
                  product.quantity !== undefined && product.quantity !== null
                    ? Number(product.quantity)
                    : product.qty !== undefined && product.qty !== null
                    ? Number(product.qty)
                    : 0;
                const unitVal =
                  product.unitValue !== undefined && product.unitValue !== null
                    ? Number(product.unitValue)
                    : product.value !== undefined && product.value !== null
                    ? Number(product.value)
                    : 0;
                return {
                  hsnCode: product.HSNCode || product.hsnCode || '',
                  qty: qtyVal,
                  quantity: qtyVal,
                  value: unitVal,
                  unitValue: unitVal,
                  description: product.description || '',
                };
              }),
            };
          }),
          unitOfMeasurement: shipmentType.unit,
        },
        document: shipmentType.type === SHIPMENT_TYPES[1].value,
        insurance: packageData.insurance,
        packaging: packageData.packaging,
        currency: shipmentType.currency,
        dropOff: pickupType.type === 'dropOff' ? true : false,
        organizationName: name === 'Cargo Solutions Network',
        rateFor: 'create-shipment' as const,
        ...(isDutyChargesAllowedSubdomain() &&
          getDutyChargeType(shipmentType.dutyCharge) && {
            dutyCharge: getDutyChargeType(shipmentType.dutyCharge),
          }),
      };

      dispatch(getRates([internationalPayload, false]));
    } else {
      // Domestic flow
      const obj = {
        destination: { ...getAddressObjLocal(consignee) },
        source: { ...getAddressObjLocal(selectedWearhouseAddress?.label ? selectedWearhouseAddress : shipper) },
        shipmentType: shipmentType.type,
        userId,
        weight: chareableWeight?.maxChargeableWt || 0,
        value,
        package: {
          box: boxes.map(box => ({
            weight: box.weight ? box.weight.toString() : '0',
            length: box.length || 0,
            width: box.width || 0,
            height: box.height || 0,
          })),
          unitOfMeasurement: shipmentType.unit,
        },
        document: shipmentType.type === SHIPMENT_TYPES[1].value,
        insurance: packageData.insurance,
        packaging: packageData.packaging,
        dropOff: pickupType.type === 'dropOff' ? true : false,
        ...(isDutyChargesAllowedSubdomain() &&
          getDutyChargeType(shipmentType.dutyCharge) && {
            dutyCharge: getDutyChargeType(shipmentType.dutyCharge),
          }),
      };
      dispatch(
        getRates([{ ...obj, organizationName: name === 'Cargo Solutions Network', rateFor: 'create-shipment' }, false]),
      );
    }
  }

  function handleBookCSP(CSPCharge: CSPChargeType) {
    if (!boxValidation || pendingRequirements.length > 0) {
      window.dispatchEvent(new CustomEvent('validate-shipment-details'));
      if (pendingRequirements.length > 0) {
        const first = pendingRequirements[0];
        enqueueSnackbar(`Please complete details: ${first.label}`, { variant: 'warning' });
        if (first.boxIndex !== undefined && boxes[first.boxIndex]) {
          setSelectedBox(boxes[first.boxIndex]);
        }
        const vatField = document.querySelector('input[name="recipientVatTaxId"]') as HTMLElement;
        if (first.label.includes('Recipient VAT') && vatField) {
          vatField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          vatField.focus();
        } else {
          const boxDimensionsEl = document.querySelector('.sb-textfield-wrapper');
          boxDimensionsEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        enqueueSnackbar('Please verify all shipment details before proceeding.', { variant: 'warning' });
      }
      return;
    }

    if (
      isShipmentExport &&
      selectedCSP &&
      (selectedCSP?._systemId === CSPCharge?._systemId || selectedCSP?.serviceName === CSPCharge?.serviceName)
    ) {
      setActiveStep(2);
      return;
    }

    setSelectedCSP(CSPCharge);
    if (!isShipmentExport) {
      setActiveStep(2); // In domestic flow, Step 3 is Summary (index 2)
    } else {
      // For export shipments, default dutyCharge to DDU if not set
      const currentDuty =
        typeof shipmentType?.dutyCharge === 'object' ? shipmentType.dutyCharge?.type : shipmentType?.dutyCharge;
      if (!currentDuty) {
        const currentCharge = typeof shipmentType?.dutyCharge === 'object' ? shipmentType.dutyCharge.charge || 0 : 0;
        const bookChargeDuty = getDutyCharge(CSPCharge?.pricing);
        setShipmentType({
          ...shipmentType,
          dutyCharge: {
            type: 'DDU',
            charge: currentCharge || bookChargeDuty || Number(CSPChargesData?.dutyChargeAmount) || 0,
          },
        });
      }
    }
  }

  function handleBackCSP() {
    setShowRates(false);
  }

  // Reset dutyCharge to DDU when export shipment becomes false
  useEffect(() => {
    if (!isShipmentExport && shipmentType?.dutyCharge) {
      setShipmentType({
        ...shipmentType,
        dutyCharge: 'DDU',
      });
    }
  }, [isShipmentExport]);

  const freightAmount =
    shipmentType?.freight !== undefined && shipmentType?.freight !== ''
      ? shipmentType.freight
      : extraDetails?.freight ?? 0;
  const insuranceAmount =
    shipmentType?.freightInsurance !== undefined && shipmentType?.freightInsurance !== ''
      ? shipmentType.freightInsurance
      : extraDetails?.freightInsurance ?? 0;
  const numericFreight = Number(freightAmount) || 0;
  const numericInsurance = Number(insuranceAmount) || 0;
  const numericShipmentValue = Number(value) || 0;
  const customsValue = numericShipmentValue + numericFreight + numericInsurance;
  const currencyLabel = shipmentType?.currency || 'INR';

  const hurricaneData = CSPChargesData?.hurricaneData;
  const dutyChargeAmount = CSPChargesData?.dutyChargeAmount;
  const selectedOrFirstPricing = selectedCSP?.pricing || CSPChargesData?.charges?.[0]?.pricing;
  const pricingDuty = getDutyCharge(selectedOrFirstPricing);
  const effectiveDuty =
    pricingDuty > 0
      ? pricingDuty
      : dutyChargeAmount !== undefined && dutyChargeAmount !== null
      ? Number(dutyChargeAmount)
      : 0;

  const dutyTaxFormatted = useMemo(() => {
    if (hurricaneData) {
      const dutyAmount =
        hurricaneData.duty && typeof hurricaneData.duty.amount === 'number' ? hurricaneData.duty.amount : 0;
      const taxAmount =
        hurricaneData.tax && typeof hurricaneData.tax.amount === 'number' ? hurricaneData.tax.amount : 0;
      const total = dutyAmount + taxAmount;
      if (total > 0) {
        const currencyVal =
          hurricaneData.duty?.currency || hurricaneData.tax?.currency || hurricaneData.currency || currencyLabel;
        return `${total.toFixed(2)} ${currencyVal}`;
      }
    }
    if (effectiveDuty > 0) {
      return `${currencyLabel} ${effectiveDuty.toFixed(2)}`;
    }
    return `${currencyLabel} 0.00`;
  }, [hurricaneData, effectiveDuty, currencyLabel]);

  const rawAccuracy =
    selectedCSP?.accuracy || (selectedCSP as any)?.accuracy || hurricaneData?.accuracy || CSPChargesData?.accuracy;
  const accuracy = rawAccuracy
    ? String(rawAccuracy).charAt(0).toUpperCase() + String(rawAccuracy).slice(1).toLowerCase()
    : '';

  const currentDutyType =
    (typeof shipmentType?.dutyCharge === 'object' ? shipmentType?.dutyCharge?.type : shipmentType?.dutyCharge) || 'DDU';

  const handleDutyChargeChange = (selectedType: 'DDU' | 'DDP') => {
    const currentCharge = typeof shipmentType?.dutyCharge === 'object' ? shipmentType.dutyCharge.charge || 0 : 0;
    const resolvedCharge = currentCharge || effectiveDuty;
    setShipmentType({
      ...shipmentType,
      dutyCharge: {
        type: selectedType,
        charge: resolvedCharge,
      },
    });
  };

  const handleProceedToSummary = () => {
    const currentType =
      typeof shipmentType?.dutyCharge === 'object' ? shipmentType.dutyCharge?.type : shipmentType?.dutyCharge;
    if (!currentType) {
      enqueueSnackbar('Please select Duty & Tax Billing term (DDU or DDP)', { variant: 'warning' });
      return;
    }
    setActiveStep(2);
  };

  const handleSaveDraft = () => {
    const { warehouse } = pickupType;
    const packageObj = getSingleShipmentPackagePayload({
      boxes,
      packageData,
      shipmentType,
      pickupType,
    });

    const sDetails = { ...shipper };
    if (!sDetails.telephoneCode)
      sDetails.telephoneCode = organizationCountry?.contactInfo?.telephoneCode?.toString() || '';
    if (!sDetails.altContactTelephoneCode)
      sDetails.altContactTelephoneCode = organizationCountry?.contactInfo?.telephoneCode?.toString() || '';

    const postObj: DraftShipmentCreatePayload = {
      package: packageObj,
      shipper: getAddressObj(sDetails),
      consignee: getAddressObj(consignee),
      shipmentNotes: notes,
      shipmentMode: shipmentType.shipmentMode,
      extraDetails: {
        shipper: {
          type: shipper.type,
          IdType: shipper.IdType,
          IdNumber: shipper.IdNumber,
        },
        consignee: {
          type: consignee.type,
          IdType: consignee.IdType,
          IdNumber: consignee.IdNumber,
        },
        other: {
          ...extraDetails,
          incoterm: shipmentType?.incoterm || extraDetails?.incoterm || 'FOB',
          freight:
            shipmentType?.freight !== undefined
              ? Number(shipmentType.freight)
              : extraDetails?.freight !== undefined
              ? Number(extraDetails.freight)
              : 0,
          freightInsurance:
            shipmentType?.freightInsurance !== undefined
              ? Number(shipmentType.freightInsurance)
              : extraDetails?.freightInsurance !== undefined
              ? Number(extraDetails.freightInsurance)
              : 0,
          iossNumber: shipmentType?.iossNumber || extraDetails?.iossNumber || '',
          recipientVatTaxId: shipmentType?.recipientVatTaxId || extraDetails?.recipientVatTaxId || '',
          iorEoriNumber: shipmentType?.iorEoriNumber || extraDetails?.iorEoriNumber || extraDetails?.ior || '',
          ior: shipmentType?.iorEoriNumber || extraDetails?.ior || '',
        },
      },
      _onbehalf: userId ? userId : null,
    };

    if (pickupType.type === 'dropOff') {
      postObj._warehouseId = warehouse;
    }

    if (draftId) {
      dispatch(
        updateDraftShipmentById({
          id: draftId,
          payload: postObj,
          navigate: '/shipment/draft?tab=saved',
          showModal: true,
        }),
      );
    } else {
      dispatch(createDraftShipment({ payload: postObj, navigate: '/shipment/draft?tab=saved', showModal: true }));
    }
  };

  const hasServices = Boolean(showRates && !isRatesStale && CSPChargesData?.charges?.length);

  // Default dutyCharge to DDU for export shipments when services are available
  useEffect(() => {
    if (isShipmentExport && hasServices) {
      const currentDuty =
        typeof shipmentType?.dutyCharge === 'object' ? shipmentType.dutyCharge?.type : shipmentType?.dutyCharge;
      if (!currentDuty) {
        setShipmentType({
          ...shipmentType,
          dutyCharge: {
            type: 'DDU',
            charge: effectiveDuty,
          },
        });
      }
    }
  }, [isShipmentExport, hasServices, effectiveDuty, shipmentType]);

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      {/* Top Section Header */}
      {!removeShipmentInfo && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
            Shipment Details & Services
          </Typography>
          <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary' }}>
            Specify package dimensions, goods details, and select a carrier service.
          </Typography>
        </Box>
      )}

      <Grid container spacing={2}>
        {!removeShipmentInfo && (
          <Grid item xs={12}>
            <ShipmentTypeDetails />
          </Grid>
        )}

        <Grid item xs={12}>
          <TotalValues />
        </Grid>

        <Grid item xs={12} sx={{ pt: '4px !important', pb: '0 !important' }}>
          <PackageAiValidationAlert validationState={validationStates['shipmentPackage']} />
        </Grid>

        {showViewMoreAction && (
          <Grid container justifyContent="flex-end">
            <ViewAction setViewMore={setViewMore} viewMore={viewMore} />
          </Grid>
        )}

        {(viewMore || !showViewMoreAction) && (
          <Grid container item xs={12} className="my-3" spacing={2}>
            <Grid item lg={4} xl={3} md={4} sm={12} xs={12}>
              <BoxList writeAccess={isShipmentDetailsWriteAllowed} />
            </Grid>
            <Grid item lg={8} xl={9} md={8} sm={12} xs={12}>
              <ProductDescription writeAccess={isShipmentDetailsWriteAllowed} />
            </Grid>
          </Grid>
        )}
      </Grid>

      {/* Rates Stale Warning Banner */}
      {!removeShipmentInfo && isRatesStale && (
        <Paper
          elevation={0}
          sx={{
            mt: 3,
            p: 2,
            borderRadius: '12px',
            border: `1px solid ${alpha(theme.palette.warning.main, 0.35)}`,
            bgcolor: alpha(theme.palette.warning.main, 0.05),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 260, flex: 1 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '10px',
                bgcolor: alpha(theme.palette.warning.main, 0.12),
                color: theme.palette.warning.dark || theme.palette.warning.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <WarningAmberOutlinedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
                Shipment details have changed
              </Typography>
              <Typography variant="body2" sx={{ color: '#475467', fontSize: '0.8125rem', mt: 0.25 }}>
                Box dimensions, weights, or shipment specifications were modified after rates were calculated. Please
                recalculate rates to view updated services and pricing.
              </Typography>
            </Box>
          </Box>
          <SbButton
            variant="contained"
            color="warning"
            size="small"
            onClick={handleCalculateRatesClick}
            disabled={isCalculateRatesDisabled}
            startIcon={
              rates.loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <LocalShippingOutlinedIcon sx={{ fontSize: 18 }} />
              )
            }
            sx={{
              px: 2.5,
              py: 0.85,
              fontWeight: 600,
              fontSize: '0.825rem',
              borderRadius: '8px',
              whiteSpace: 'nowrap',
            }}
          >
            {rates.loading ? 'Recalculating...' : 'Recalculate Rates'}
          </SbButton>
        </Paper>
      )}

      {/* Pending Rate Requirements Indicator */}
      {!removeShipmentInfo && pendingRequirements.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            mt: 3,
            p: 2,
            borderRadius: '12px',
            border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
            bgcolor: alpha(theme.palette.info.main, 0.03),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <InfoOutlinedIcon sx={{ color: 'info.main', fontSize: 20 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
              Pending requirements to calculate rates:
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {pendingRequirements.map((req, i) => (
              <Chip
                key={i}
                label={req.label}
                size="small"
                onClick={() => {
                  if (req.boxIndex !== undefined && boxes[req.boxIndex]) {
                    setSelectedBox(boxes[req.boxIndex]);
                  }
                }}
                sx={{
                  bgcolor: alpha(theme.palette.warning.main, 0.08),
                  color: theme.palette.warning.dark || '#b78103',
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  cursor: req.boxIndex !== undefined ? 'pointer' : 'default',
                  '&:hover': req.boxIndex !== undefined ? { bgcolor: alpha(theme.palette.warning.main, 0.16) } : {},
                }}
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* Shipment Details Action Controls */}
      {!removeShipmentInfo && (
        <Paper
          elevation={0}
          sx={{
            mt: 3,
            mb: 3,
            p: 2,
            borderRadius: '12px',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
            bgcolor: theme.palette.background.paper,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <SbButton
              variant="outlined"
              onClick={() => setActiveStep(0)}
              startIcon={<ArrowBackIcon />}
              sx={{
                px: 2.5,
                py: 1,
                fontWeight: 600,
                borderRadius: '8px',
              }}
            >
              Back to Shipper & Consignee
            </SbButton>
            <SbButton
              variant="outlined"
              onClick={handleSaveDraft}
              sx={{
                px: 2.5,
                py: 1,
                fontWeight: 600,
                borderRadius: '8px',
              }}
            >
              Save as draft
            </SbButton>
          </Box>

          <SbButton
            variant="contained"
            onClick={handleCalculateRatesClick}
            disabled={isCalculateRatesDisabled}
            startIcon={rates.loading ? <CircularProgress size={18} color="inherit" /> : <LocalShippingOutlinedIcon />}
            sx={{
              px: 3.5,
              py: 1.2,
              fontWeight: 600,
              fontSize: '0.925rem',
              borderRadius: '10px',
              bgcolor: isCalculateRatesDisabled ? alpha(theme.palette.action.disabledBackground, 0.6) : undefined,
              color: isCalculateRatesDisabled ? theme.palette.text.disabled : undefined,
              boxShadow: isCalculateRatesDisabled ? 'none' : `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
              cursor: isCalculateRatesDisabled ? 'not-allowed' : 'pointer',
              '&:hover': {
                bgcolor: isCalculateRatesDisabled ? alpha(theme.palette.action.disabledBackground, 0.6) : undefined,
              },
            }}
          >
            {rates.loading
              ? 'Calculating Rates...'
              : isRatesStale
              ? 'Recalculate Rates & Show Services'
              : hasServices
              ? 'Refresh Available Services'
              : 'Calculate Rates & Show Services'}
          </SbButton>
        </Paper>
      )}

      {/* Services Available Section (Embedded in Step 2) */}
      <div ref={servicesRef}>
        {rates.loading && (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              my: 3,
              borderRadius: '16px',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              bgcolor: alpha(theme.palette.primary.main, 0.02),
              textAlign: 'center',
            }}
          >
            <CircularProgress size={36} color="primary" sx={{ mb: 1.5 }} />
            <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>
              Fetching Available Carrier Rates & Services
            </Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary', mt: 0.5 }}>
              Comparing best available routes, transit times, and contract pricing...
            </Typography>
          </Paper>
        )}

        {hasServices && !rates.loading && (
          <Box sx={{ mt: 2, mb: 4 }}>
            {/* Duty & Tax Billing Section (Above Select Service, only for export shipments) */}
            {(() => {
              const shouldRenderDutyTaxSection = Boolean(isShipmentExport);

              // TODO: remove after verification
              console.info('[DutyTax] render gate', {
                isExportShipment: isShipmentExport,
                selectedServiceId: selectedCSP?._systemId || selectedCSP?.serviceName || null,
                shouldRenderDutyTaxSection,
              });

              if (!isShipmentExport) return null;

              return (
                <Paper
                  ref={dutyTaxSectionRef}
                  elevation={0}
                  sx={{
                    mb: 3,
                    p: { xs: 2, sm: 3 },
                    borderRadius: '14px',
                    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
                    bgcolor: theme.palette.background.paper,
                  }}
                >
                  {/* Section Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ReceiptLongOutlinedIcon />
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'text.primary' }}>
                          Who pays duty & taxes? (DDP / DDU)
                        </Typography>
                        <Chip
                          label="Required"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.6875rem',
                            fontWeight: 600,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: 'primary.main',
                          }}
                        />
                      </Box>
                      <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary', mt: 0.25 }}>
                        Specify how import customs duties and destination taxes should be billed
                      </Typography>
                    </Box>
                  </Box>

                  {/* Computed Values Summary */}
                  <Grid container spacing={2} sx={{ mb: 2.5 }}>
                    <Grid item xs={12} sm={6}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: '10px',
                          bgcolor: alpha(theme.palette.background.default, 0.6),
                          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                        }}
                      >
                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                          Customs Value (Shipment + Freight + Insurance)
                        </Typography>
                        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: 'text.primary' }}>
                          {currencyLabel} {customsValue.toFixed(2)}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: '10px',
                          bgcolor: alpha(theme.palette.background.default, 0.6),
                          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography sx={{ fontSize: '13px', fontWeight: 500, color: 'text.secondary' }}>
                            Duty & Taxes Value {selectedCSP ? `(${selectedCSP?.serviceName})` : ''}
                          </Typography>
                          {selectedCSP && accuracy && (
                            <Chip
                              label={`Accuracy: ${accuracy}`}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                bgcolor: alpha(theme.palette.info.main, 0.1),
                                color: 'info.main',
                              }}
                            />
                          )}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: selectedCSP ? '18px' : '14px',
                            fontWeight: selectedCSP ? 700 : 500,
                            color: selectedCSP ? 'primary.main' : 'text.secondary',
                            fontStyle: selectedCSP ? 'normal' : 'italic',
                          }}
                        >
                          {dutyTaxFormatted || 'Select a service below to view duty & taxes'}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  {/* Segmented DDP / DDU Selector */}
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary', mb: 1 }}>
                      Choose Billing Term *
                    </Typography>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        p: 0.5,
                        bgcolor: alpha(theme.palette.divider, 0.08),
                        borderRadius: '10px',
                        gap: 1,
                      }}
                    >
                      <SbButton
                        variant={currentDutyType === 'DDU' ? 'contained' : 'text'}
                        onClick={() => handleDutyChargeChange('DDU')}
                        sx={{
                          px: 3,
                          py: 1,
                          borderRadius: '8px',
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          bgcolor: currentDutyType === 'DDU' ? 'primary.main' : 'transparent',
                          color: currentDutyType === 'DDU' ? 'common.white' : 'text.primary',
                          boxShadow:
                            currentDutyType === 'DDU' ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
                          '&:hover': {
                            bgcolor:
                              currentDutyType === 'DDU' ? 'primary.dark' : alpha(theme.palette.action.hover, 0.1),
                          },
                        }}
                      >
                        DDU (Delivered Duty Unpaid)
                      </SbButton>
                      <SbButton
                        variant={currentDutyType === 'DDP' ? 'contained' : 'text'}
                        onClick={() => handleDutyChargeChange('DDP')}
                        sx={{
                          px: 3,
                          py: 1,
                          borderRadius: '8px',
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          bgcolor: currentDutyType === 'DDP' ? 'primary.main' : 'transparent',
                          color: currentDutyType === 'DDP' ? 'common.white' : 'text.primary',
                          boxShadow:
                            currentDutyType === 'DDP' ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
                          '&:hover': {
                            bgcolor:
                              currentDutyType === 'DDP' ? 'primary.dark' : alpha(theme.palette.action.hover, 0.1),
                          },
                        }}
                      >
                        DDP (Delivered Duty Paid)
                      </SbButton>
                    </Box>

                    {/* Helper text under selector */}
                    <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={currentDutyType === 'DDP' ? 'Added to your charges' : 'Consignee pays on delivery'}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          bgcolor:
                            currentDutyType === 'DDP'
                              ? alpha(theme.palette.primary.main, 0.12)
                              : alpha(theme.palette.success.main, 0.12),
                          color: currentDutyType === 'DDP' ? 'primary.main' : 'success.dark',
                        }}
                      />
                      <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                        {currentDutyType === 'DDP'
                          ? 'Import duty & taxes are added to your invoice and prepaid. Consignee pays nothing upon delivery.'
                          : 'Import duty & taxes will be collected directly from the consignee upon delivery.'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* EU-Only Info Note */}
                  {isEUDestination && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: '10px',
                        bgcolor: alpha(theme.palette.info.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                        <InfoOutlinedIcon sx={{ color: 'info.main', fontSize: 20, mt: 0.25 }} />
                        <Box>
                          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'info.dark', mb: 0.5 }}>
                            Goods value {currencyLabel} {Number(value || 0).toFixed(2)}
                            {eurEquivalentText ? ` ${eurEquivalentText}` : ''} · €3 duty per line
                          </Typography>
                          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'info.dark', mb: 0.5 }}>
                            Final duty may differ
                          </Typography>
                          <Typography sx={{ fontSize: '12px', color: 'text.secondary', lineHeight: 1.6 }}>
                            • Item 2: HS code has 6 digits, so the rate was matched at a broader level
                            <br />
                            • EU flat €3 per line applied: a fixed amount
                            <br />• Final duty and taxes are set by destination customs.
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  )}
                </Paper>
              );
            })()}

            {/* Select Service Cards Section */}
            <ShipmentServices handleBookCSP={handleBookCSP} setShowRates={handleBackCSP} />

            {/* Proceed to Summary Action (below services after service selected) */}
            {selectedCSP && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <SbButton
                  variant="contained"
                  onClick={handleProceedToSummary}
                  disabled={!currentDutyType}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    px: 4,
                    py: 1.2,
                    fontWeight: 700,
                    fontSize: '0.925rem',
                    borderRadius: '10px',
                    boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
                  }}
                >
                  Continue to Shipment Summary
                </SbButton>
              </Box>
            )}
          </Box>
        )}
      </div>

      <NoCSPFound setShowRates={setShowRates} />
    </Box>
  );
}

export default ShipmentDetails;
